import React, { useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import { Search, Star, X } from 'lucide-react';

/**
 * CoinSearch — instant autocomplete over the Binance.US universe with a
 * CoinGecko fallback for tokens the exchange doesn't list. Server-
 * ranked so BTC ranks above @BTC25USDT.
 */
export default function CoinSearch({ onPick, current }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    // debounced fetch — 120ms is under human perception, avoids storm
    clearTimeout(debounceRef.current);
    const query = q.trim();
    if (!query) { setRows([]); return; }
    debounceRef.current = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get('/crypto/search', { params: { q: query, limit: 20 } });
        setRows(data?.results || []);
        setOpen(true);
      } catch { setRows([]); }
      finally { setBusy(false); }
    }, 120);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (row) => {
    onPick?.(row);
    setQ('');
    setOpen(false);
    setRows([]);
  };

  return (
    <div ref={boxRef} className="relative" data-testid="crypto-search">
      <div className="flex items-center gap-2 border border-[var(--border-default)] focus-within:border-cyan-sys bg-black px-2 py-1.5">
        <Search size={13} className="text-[var(--txt-muted)]" />
        <input
          ref={inputRef}
          data-testid="crypto-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => rows.length && setOpen(true)}
          placeholder={current ? `Search coins… (current: ${current})` : 'Search 10,000+ coins (BTC, ETH, SUI, PEPE…)'}
          className="flex-1 bg-transparent outline-none text-[12px] font-mono text-cyan-sys placeholder:text-[var(--txt-muted)]"
        />
        {q && (
          <button onClick={() => { setQ(''); setRows([]); }} className="text-[var(--txt-muted)] hover:text-sell">
            <X size={12} />
          </button>
        )}
      </div>
      {open && (rows.length > 0 || busy) && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 border border-cyan-sys bg-[var(--bg-panel)] shadow-[0_0_28px_rgba(0,229,255,0.12)] max-h-96 overflow-y-auto">
          {busy && rows.length === 0 && (
            <div className="text-[10px] px-3 py-2 text-[var(--txt-muted)] uppercase tracking-widest">
              // scanning universe…
            </div>
          )}
          {rows.map((r) => (
            <button
              key={r.symbol}
              onClick={() => pick(r)}
              data-testid={`crypto-search-row-${r.symbol}`}
              className="w-full flex items-center justify-between gap-3 px-3 py-2 hover:bg-cyan-sys hover:text-black border-b border-[var(--border-default)] last:border-b-0 text-left"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[11px] font-mono text-cyan-sys group-hover:text-black tracking-tight">
                  {r.base}
                </span>
                <span className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)]">
                  {r.quote}
                </span>
                {r.provider === 'coingecko' && (
                  <span className="text-[9px] uppercase tracking-widest text-hold border border-hold px-1">
                    meta-only
                  </span>
                )}
              </div>
              <div className="text-[10px] text-[var(--txt-secondary)] font-mono truncate">
                {r.name || r.symbol}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
